/**
 * 10.9BY — BONECO DE TREINO: estrutura do hub (leste do anel) onde o Caçador contrata TREINO
 * OFFLINE de skills (tempo real, pago em 🪙/h). Poste de madeira + boneco (torso cilíndrico,
 * cabeça, braços) com um alvo circular no peito que pulsa; quando há treino ATIVO o anel do
 * chão acende (o jogador vê de longe que tem coisa rendendo).
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { makeLabel, disposeLabel } from "./StructureLabel";
import { energyMat, flatMat, metalMat, platformMat, woodMat } from "./Materials";

export class TrainingDummy {
  readonly group = new THREE.Group();
  private readonly pos: THREE.Vector3;
  private ring!: THREE.Mesh;
  private target!: THREE.Mesh;
  private body!: THREE.Group;
  private time = 0;
  private active = false;
  private labelHolder!: THREE.Group; // 10.9DQ
  private label: THREE.Object3D | null = null;
  private labelKey = "";

  constructor(scene: THREE.Scene, position: { x: number; z: number }) {
    this.pos = new THREE.Vector3(position.x, 0, position.z);
    this.buildMesh();
    this.group.position.copy(this.pos);
    scene.add(this.group);
  }

  get position(): THREE.Vector3 { return this.pos; }

  setVisible(v: boolean): void { this.group.visible = v; }

  /** 10.9DQ — texto flutuante do totem (mostra o tempo de treino aqui, não no menu). */
  setLabel(text: string): void {
    if (text === this.labelKey) return;
    this.labelKey = text;
    disposeLabel(this.label);
    this.label = makeLabel(text, "#ffb020", 3.6);
    this.labelHolder.add(this.label);
  }

  isPlayerNear(playerPos: THREE.Vector3, radius = GameConfig.skills.training.interactRadius): boolean {
    return playerPos.distanceTo(this.pos) <= radius;
  }

  /** Treino em andamento → anel do chão aceso. */
  setTraining(on: boolean): void {
    this.active = on;
    this.ring.visible = on;
  }

  update(dt: number): void {
    this.time += dt;
    // alvo pulsa; boneco balança de leve (como se tivesse levado um golpe)
    const s = 1 + Math.sin(this.time * 3) * 0.06;
    this.target.scale.set(s, s, 1);
    this.body.rotation.z = Math.sin(this.time * 1.6) * 0.04;
    if (this.active) this.ring.rotation.y = this.time * 0.9;
  }

  private buildMesh(): void {
    // 10.9BQ — tudo da biblioteca de materiais (nada anima material aqui)
    const wood = woodMat();
    const base = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.2, 0.18, 20), platformMat(0x3a2a10, 0.25));
    base.position.y = 0.09;
    this.group.add(base);

    // poste
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.13, 1.6, 8), wood);
    post.position.y = 0.8;
    post.castShadow = true;
    this.group.add(post);

    // boneco (grupo pra balançar junto)
    this.body = new THREE.Group();
    this.body.position.y = 1.55;
    this.group.add(this.body);
    const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.4, 1.1, 10), metalMat(0x8a6a3a));
    torso.position.y = 0.2;
    torso.castShadow = true;
    this.body.add(torso);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.26, 12, 10), wood);
    head.position.y = 0.98;
    head.castShadow = true;
    this.body.add(head);
    const armGeo = new THREE.BoxGeometry(0.7, 0.12, 0.12);
    const armL = new THREE.Mesh(armGeo, wood);
    armL.position.set(-0.55, 0.5, 0);
    armL.rotation.z = 0.35;
    this.body.add(armL);
    const armR = new THREE.Mesh(armGeo, wood);
    armR.position.set(0.55, 0.5, 0);
    armR.rotation.z = -0.35;
    this.body.add(armR);
    // alvo circular no peito (voltado pro centro do hub = oeste)
    this.target = new THREE.Mesh(new THREE.CircleGeometry(0.26, 20), energyMat(0xff4d6d, "disc"));
    this.target.position.set(-0.41, 0.28, 0);
    this.target.rotation.y = -Math.PI / 2;
    this.body.add(this.target);
    const bull = new THREE.Mesh(new THREE.CircleGeometry(0.09, 12), flatMat(0xfff1d0));
    bull.position.set(-0.415, 0.28, 0);
    bull.rotation.y = -Math.PI / 2;
    this.body.add(bull);

    // anel no chão — só quando tem treino ativo
    this.ring = new THREE.Mesh(new THREE.RingGeometry(1.25, 1.55, 24, 1, 0, Math.PI * 1.75), energyMat(0xffb020, "ring"));
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.position.y = 0.05;
    this.ring.visible = false;
    this.group.add(this.ring);

    this.labelHolder = new THREE.Group();
    this.labelHolder.position.y = 0;
    this.group.add(this.labelHolder);
    this.setLabel("🎯 Treino");
  }
}
